import { PopoverContext } from '@context/popover-context';
import useClickBoundary from '@hooks/use-click-boundary';
import useId from '@hooks/use-id';
import useIsomorphicLayoutEffect from '@hooks/use-isomorphic-layout-effect';
import useNode from '@hooks/use-node';
import useResponsiveRect from '@hooks/use-responsive-rect';
import useSimulateTabNavigationForReactTree from '@hooks/use-simulate-tab-navigation-for-react-tree';
import { noop, throttleDebounce } from '@modules/helpers';
import { positionBounded } from '@modules/popover-helpers';
import { Portal } from '@reach/portal';
import { useComposedRefs } from '@reach/utils';
import { useCallback, useState } from 'react';

import styles from './Popover.module.scss';

/* Close event - sometimes we want the popover to close as soon as the mouse presses down
to avoid any unwanted sideeffects whilst the mouse is held. For example on the editor we want
the command popover to close immediately because mousedown causes the selection to change so whilst
it's held down the contents of the popover will change. The NavBar profile links popover we want to
stay open whilst the mouse is down because buttons need an onclick handler to work with the keyboard
*/

////////////////////////////////////////////////////////////////////////////////
// Popover

// If the target ref is an element that can recieve focus and it is currently focused
// then this will also take care of making sure the next tab takes you to the popover (if
// it has focusable elements)

export type Positioner = ReturnType<typeof positionBounded>;

interface PopoverImplProps {
  positioner?: Positioner;
  style?: { [key: string]: string };
  show: boolean;
  fixed?: boolean;
  positionDeps?: React.DependencyList;
  matchWidth?: 'strict' | 'min';
  focusable?: boolean;
  onClose: (event: MouseEvent | React.KeyboardEvent) => void;
  onBlur?: () => void;
  /** Prevents the popover from closing when the mouse clicks outside the bounding box */
  preventCloseOnClickOutsidePopover?: boolean;
  /**
   * This is useful if we have a popover inside another popover and we want to prevent the parent popover from
   * closing when interacting with the child. This works by prevent propogation which stops the parent popover from
   * registering that the event happened since events bubble through portals in React
   */
  preventInteractionBubbling?: boolean;
  targetNode?: HTMLElement;
  targetRect?: DOMRect;
  size?: 'regular' | 'large';
  /** Defaults to `true` - if `true`, the popover will apply some extra modal styles,
   * such as drawing a box-shadow around `children` and a white background color behind `children` */
  applyModalStyle?: boolean;
}

interface PopoverProps extends PopoverImplProps {
  preventOutsideInteraction?: boolean;
  drawFocus?: boolean;
}

const Popover = function Popover({
  ref,
  ...props
}: React.PropsWithChildren<PopoverProps> & {
  ref?: React.Ref<HTMLDivElement | undefined>;
}) {
  const {
    preventOutsideInteraction = false,
    preventCloseOnClickOutsidePopover = false,
    drawFocus = false,
    ...rest
  } = props;
  const { show, positionDeps } = rest;

  // This is a bit of a hack - every time an update occurs that would change the size of the popover
  // we just need to call handlePositionDepsChange so I can increment this number. This will cause the
  // positionDeps to change which will recalculate the popovers size.
  const [posDepsChangeTracker, setPosDepsChangeTracker] = useState(0);

  const handlePositionDepsChange = useCallback(() => {
    setPosDepsChangeTracker((num) => num + 1);
  }, []);

  // We don't need to detect click events on the overlay div since the popover already monitors for clicks outside of itself
  return (
    <Portal>
      <PopoverContext.Provider value={{ show, onPositionDepsChange: handlePositionDepsChange }}>
        {preventOutsideInteraction && (
          <div
            className={styles['overlay']}
            data-show={show}
            aria-hidden="true"
            data-draw-focus={drawFocus}
          ></div>
        )}
        <PopoverImpl
          onBlur={props.onBlur}
          ref={ref}
          {...rest}
          preventCloseOnClickOutsidePopover={preventCloseOnClickOutsidePopover}
          positionDeps={[...(positionDeps || []), posDepsChangeTracker]}
        />
      </PopoverContext.Provider>
    </Portal>
  );
};

Popover.displayName = 'Popover';

export default Popover;

export * from '@modules/popover-helpers';

////////////////////////////////////////////////////////////////////////////////
// PopoverImpl

// Popover is conditionally rendered so we can't start measuring until it shows
// up, so useRect needs to live down here not up in Popover
const PopoverImpl = function PopoverImpl({
  ref: forwardedRef,
  targetNode,
  targetRect: overridenTargetRect,
  positioner = positionBounded(),
  style,
  show = false,
  fixed = false,
  positionDeps = [],
  children,
  matchWidth,
  focusable = true,
  onClose,
  onBlur,
  preventCloseOnClickOutsidePopover,
  preventInteractionBubbling,
  size = 'regular',
  applyModalStyle = true,
  ...rest
}: React.PropsWithChildren<PopoverImplProps> & {
  ref?: React.Ref<HTMLDivElement | undefined>;
}) {
  const [popoverNode, popoverRef] = useNode<HTMLDivElement>();
  const ref = useComposedRefs(popoverRef, forwardedRef);
  const targetRefRect = useResponsiveRect(targetNode, show && !!targetNode);
  const targetRect = overridenTargetRect || targetRefRect;
  const id = useId();

  useIsomorphicLayoutEffect(() => {
    if (!show || !targetRect || !popoverNode) {
      return;
    }

    const sizeUpdater = popoverSizeUpdater(popoverNode);
    const interactionListener = () => {
      sizeUpdater(overridenTargetRect || targetNode?.getBoundingClientRect(), positioner);
    };

    const throttledResize = throttleDebounce(interactionListener, 300);

    window.addEventListener('resize', throttledResize);
    window.addEventListener('scroll', throttledResize);

    interactionListener();

    return () => {
      window.removeEventListener('resize', throttledResize);
      window.removeEventListener('scroll', throttledResize);
    };
  }, [show, targetRect, popoverNode, ...positionDeps]);

  const handleClickOutsidePopover = useCallback(
    (event: MouseEvent) => {
      if (!preventCloseOnClickOutsidePopover) {
        onClose?.(event);
      }
    },
    [onClose, preventCloseOnClickOutsidePopover],
  );

  useClickBoundary(popoverNode, false, handleClickOutsidePopover, show);

  function handleInteraction(event: React.MouseEvent | React.KeyboardEvent) {
    if (preventInteractionBubbling) {
      event.stopPropagation();
    }
  }
  function handleKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'Escape') {
      handleInteraction(event);
      onClose?.(event);
      targetNode?.focus();
    }
  }

  useSimulateTabNavigationForReactTree(targetNode, popoverNode, { disable: !focusable });

  return (
    /* eslint-disable-next-line jsx-a11y/no-static-element-interactions */
    <div
      onBlur={onBlur}
      className={styles['popover']}
      data-target-id={id}
      data-is-visible={show}
      data-fixed={fixed}
      data-size={size}
      data-apply-modal-style={applyModalStyle}
      ref={ref}
      onMouseDown={handleInteraction}
      onKeyDown={handleKeyDown}
      style={{
        ...style,
        position: fixed ? 'fixed' : 'absolute',
        width: matchWidth === 'strict' ? targetNode?.clientWidth : undefined,
        minWidth: matchWidth === 'min' ? targetNode?.clientWidth : undefined,
      }}
      {...rest}
    >
      <div data-reach-popover-content>{show && children}</div>
    </div>
  );
};

function popoverSizeUpdater(popoverNode: HTMLDivElement | undefined) {
  if (!popoverNode) {
    return noop;
  }

  // Don't need to do popover rect since we only use it's height and that won't change on resize
  const popoverRect = popoverNode?.getBoundingClientRect();
  const popoverContentHeight = (popoverNode?.firstElementChild as HTMLDivElement)?.offsetHeight;

  return (targetRect: DOMRect | undefined, positioner: PopoverImplProps['positioner']) => {
    if (!popoverNode || !targetRect) {
      return;
    }

    const newPosition = positioner?.(targetRect, popoverRect, popoverContentHeight) || {};

    for (const [key, value] of Object.entries(newPosition)) {
      if (typeof value === 'number') {
        popoverNode.style[key as keyof typeof newPosition] = `${value}px`;
      } else if (value) {
        popoverNode.style[key as keyof typeof newPosition] = value;
      }
    }
  };
}

PopoverImpl.displayName = 'PopoverImpl';
